import React from "react";
import { FileText, Signature } from "lucide-react";
import { toast } from "sonner";
import { ASG_CHIP, docApi, docErr } from "@/lib/docApi";
import { Empty, ErrorBox, Loading, SummaryChip, btnGold, useLoad, when } from "@/components/nds/windows/finance/parts";
import { SCRIPT_FONT, StatusChip } from "@/components/nds/windows/doc/parts";

export default function DocSignaturesContent() {
  const [state, load] = useLoad(() => docApi.get("/signatures"), []);
  if (state.loading && !state.data) return <Loading testid="doc-sig-loading" />;
  if (state.error) return <ErrorBox testid="doc-sig-error" error={state.error} onRetry={load} />;
  const { signatures, pending } = state.data;
  return (
    <div data-testid="doc-sig" className="flex flex-col h-full overflow-y-auto">
      <div className="px-5 py-3 border-b border-white/10 flex items-center gap-3">
        <Signature className="h-4 w-4 text-nex-gold" />
        <SummaryChip tone="yellow" testid="doc-sig-summary">{pending.length} pending signature</SummaryChip>
        <span className="text-xs text-nex-muted">{signatures.length} signed</span>
      </div>
      <section className="px-5 py-3 space-y-2">
        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">Awaiting signature</div>
        {pending.length ? pending.map((a) => (
          <div key={a.id} data-testid={`doc-sig-pending-${a.id}`} className="flex items-center gap-3 text-sm">
            <span className="flex-1 truncate text-nex-silver">{a.document_title}</span>
            <span className="text-xs text-nex-muted">{a.recipient_name} · {a.recipient_type}</span>
            <span className="font-mono text-[10px] text-nex-muted">expires {when(a.token_expires_at)}</span>
            <StatusChip map={ASG_CHIP} status={a.status} />
          </div>
        )) : <Empty testid="doc-sig-pending-empty">Nothing awaiting signature.</Empty>}
      </section>
      <section className="px-5 py-3 space-y-2 border-t border-white/10">
        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">Signed (read-only)</div>
        {signatures.length ? signatures.map((s) => (
          <div key={s.id} data-testid={`doc-sig-row-${s.id}`} className="flex flex-wrap items-center gap-3 border border-white/10 rounded-sm px-3 py-2">
            <div className="flex-1 min-w-[200px]">
              <div className="text-nex-silver text-sm">{s.document_title}</div>
              <div className="text-xs text-nex-muted">{s.recipient_name} · {s.recipient_type} · {when(s.signed_at)} · IP {s.ip_address || "unknown"}</div>
            </div>
            <span className="text-2xl text-white" style={SCRIPT_FONT}>{s.typed_name}</span>
            <button type="button" onClick={() => docApi.openPdf(`/signatures/${s.id}/pdf`).catch((e) => toast.error(docErr(e)))} data-testid={`doc-sig-pdf-${s.id}`} className={`${btnGold} inline-flex items-center gap-1`}><FileText className="h-3 w-3" /> Signed PDF</button>
          </div>
        )) : <Empty testid="doc-sig-empty">No signatures yet.</Empty>}
      </section>
    </div>
  );
}
